import { SrInsightType as InsightType } from '@plato/data/starrocks/types';
import { mapGroupBy } from '@plato/shared-util/map';
import { AiIcon } from '@plato/ui-legacy/assets/icons/ai-icon';
import { ProductIcon } from '@plato/ui-legacy/assets/icons/product-icon';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { Button, PolymorphicButtonBase } from '@plato/ui/primitives/button';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { ToggleButton } from '@plato/ui/primitives/toggle-button';
import { Typography } from '@plato/ui/primitives/typography';
import { cn } from '@plato/ui/util/classes';
import { ArrowUpRightIcon, PlusIcon, StarIcon, TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { ProductVariantDTO } from '../../../server/dto/product-variant-dto.ts';
import type { InsightListItem } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { InsightReasonText } from '../../insights-common/components/insight-reason-text.tsx';
import { getInsightEntityName } from '../../insights-common/lib/insight-entity-name.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';
import type { AddProductToDraftInput } from '../hooks/use-add-product-to-draft.ts';

type Product = { id: string; name: string; articleGroupId?: string | null };

type DraftSuggestionsProps = {
    insights: InsightListItem[];
    topProducts?: { source: 'customer' | 'tenant'; items: ProductVariantDTO[] };
    /** Products already on the draft, hidden from every list */
    excludeProductIds: string[];
    onSelectProduct: (product: AddProductToDraftInput) => void;
};

const topTabId = 'top';
const initialRows = 3;
const rowsStep = 10;

// ToggleButton's own checked style, in the design system's pill shape (`filter-controls.tsx`)
const tabClasses = 'gap-tight rounded-full px-default text-ui-sm';

/**
 * "Suggestions" under the draft line items: a Top tab with the customer's top products and one
 * tab per insight type, each listing its insights as category groups with the insight's reason.
 * Products added from an insight carry its id, so the insight is triaged on submit.
 */
export function DraftSuggestions({
    insights,
    topProducts,
    excludeProductIds,
    onSelectProduct,
}: DraftSuggestionsProps): ReactElement {
    const { t } = useTranslation('sales-draft');
    const [openTab, setOpenTab] = useState<string | undefined>(topTabId);

    // ponytail: both churn types share one tab, they have the same name ("Churn warning")
    const tabKey = (type: InsightType): string =>
        type === InsightType.GLOBAL_CHURN_WARNING ? InsightType.CHURN_WARNING : type;
    // mapGroupBy, not Map.groupBy: the native one crashes older iOS Safari
    const tabs = mapGroupBy(insights, (item) => tabKey(item.insight.insight_type));

    return (
        <div className="flex w-full shrink-0 flex-col gap-tight pt-cozy">
            <span className="flex items-center gap-cozy">
                <AiIcon />
                <Typography variant="subtle-heading">{t('sales_draft__suggestions_header')}</Typography>
            </span>

            {/* Tabs toggle: tapping the open one closes it again */}
            <div role="group" className="flex flex-wrap gap-cozy py-tight">
                <ToggleButton
                    aria-pressed={openTab === topTabId}
                    checked={openTab === topTabId}
                    className={tabClasses}
                    onClick={() => {
                        setOpenTab((cur) => (cur === topTabId ? undefined : topTabId));
                    }}
                >
                    <StarIcon className="size-3.5 text-content-weaker" />
                    {t('sales_draft__suggestions_tab_top')}
                </ToggleButton>
                {[...tabs].map(([key, items]) => {
                    const first = items[0]?.insight;
                    if (!first) {
                        return undefined;
                    }

                    const isChurn = key === InsightType.CHURN_WARNING;
                    const pressed = openTab === key;
                    return (
                        <ToggleButton
                            key={key}
                            aria-pressed={pressed}
                            checked={pressed}
                            className={tabClasses}
                            onClick={() => {
                                setOpenTab(pressed ? undefined : key);
                            }}
                        >
                            {isChurn ? (
                                <TrendingDownIcon className="size-3.5 text-danger" />
                            ) : (
                                <TrendingUpIcon className="size-3.5 text-success" />
                            )}
                            {isChurn
                                ? t('insight_name__ChurnWarningWebCategory', { ns: 'insights' })
                                : getInsightName(t, first)}
                            <span
                                className={cn('tabular-nums', pressed ? 'text-brand' : 'text-content-weaker')}
                            >
                                {items.length}
                            </span>
                        </ToggleButton>
                    );
                })}
            </div>

            {openTab === topTabId && topProducts && (
                <SuggestionGroup
                    reason={
                        topProducts.source === 'customer'
                            ? t('sales_draft__recommended_header__customer')
                            : t('sales_draft__recommended_header__tenant')
                    }
                    products={topProducts.items.map((p) => ({
                        id: p.product_variant_id,
                        name: p.product_variant_name,
                        articleGroupId: p.article_group_id,
                    }))}
                    excludeProductIds={excludeProductIds}
                    onSelect={(p) => {
                        onSelectProduct({ product_variant_id: p.id, article_group_id: p.articleGroupId });
                    }}
                />
            )}

            {tabs
                .get(openTab ?? '')
                ?.map((item) => (
                    <InsightSuggestionGroup
                        key={item.insight.id}
                        item={item}
                        excludeProductIds={excludeProductIds}
                        onSelectProduct={onSelectProduct}
                    />
                ))}
        </div>
    );
}

/** One insight as a category group; loads its products only once its tab is open. */
function InsightSuggestionGroup({
    item,
    excludeProductIds,
    onSelectProduct,
}: {
    item: InsightListItem;
    excludeProductIds: string[];
    onSelectProduct: (product: AddProductToDraftInput) => void;
}): ReactElement {
    const { t } = useTranslation('insights');
    const { insight } = item;
    const extendedView = api.platoUser.query.insights.getInsightExtendedView;

    // Churn warnings list the products the customer stopped buying, the rest recommend products
    // Same procedure per mount: an insight's type never changes
    const isGlobalChurn = insight.insight_type === InsightType.GLOBAL_CHURN_WARNING;
    const isChurn = isGlobalChurn || insight.insight_type === InsightType.CHURN_WARNING;
    const churnDetail = isGlobalChurn
        ? extendedView.getGlobalChurnWarningDetail
        : extendedView.getChurnWarningDetail;
    const churn = churnDetail.useQuery({ insightId: insight.id }, { enabled: isChurn });
    const recommendations = extendedView.getProductRecommendationsDetail.useQuery(
        { insightId: insight.id },
        { enabled: !isChurn },
    );

    const products: Product[] =
        (isChurn
            ? churn.data?.map((p) => ({ id: p.id, name: p.name }))
            : recommendations.data?.map((p) => ({
                  id: p.product_variant_id,
                  name: p.product_variant_name,
              }))) ?? [];
    const categoryName = getInsightEntityName(t, item);

    return (
        <SuggestionGroup
            loading={isChurn ? churn.isLoading : recommendations.isLoading}
            label={categoryName}
            reason={<InsightReasonText insight={insight} categoryName={categoryName} />}
            products={products}
            excludeProductIds={excludeProductIds}
            onSelect={(p) => {
                onSelectProduct({ product_variant_id: p.id, insight_id: insight.id });
            }}
        />
    );
}

function SuggestionGroup({
    loading,
    label,
    reason,
    products,
    excludeProductIds,
    onSelect,
}: {
    loading?: boolean;
    label?: string;
    reason: ReactElement | string;
    products: Product[];
    excludeProductIds: string[];
    onSelect: (product: Product) => void;
}): ReactElement {
    const { t } = useTranslation('common');
    const { openProductDetails } = useDialogs();
    const params = useParams<{ customer_id: string }>();
    const [shown, setShown] = useState(initialRows);
    const visible = products.filter((p) => !excludeProductIds.includes(p.id));

    return (
        <div className="flex flex-col">
            <div className="flex flex-col pt-cozy">
                {label && <Typography variant="group-heading">{label}</Typography>}
                <Typography variant="caption">{reason}</Typography>
            </div>
            {loading && <SuggestionRowsLoading />}
            {/* Same row divider as the insight dialog's article lists */}
            <div className="flex flex-col divide-y divide-line-weak">
                {visible.slice(0, shown).map((product) => (
                    <div key={product.id} className="flex items-start gap-cozy py-cozy">
                        {/* A real link to the product page; a plain click opens the product dialog instead */}
                        <PolymorphicButtonBase
                            to={`/inventory/${encodeURIComponent(product.id)}`}
                            className="group flex min-w-0 flex-1 items-start gap-cozy"
                            onClick={(e) => {
                                if (e.ctrlKey || e.metaKey) {
                                    return;
                                }

                                e.preventDefault();
                                openProductDetails(product.id, params.customer_id ?? '');
                            }}
                        >
                            {/* Icon centred on the first row box, stays put when the name wraps */}
                            <span className="flex h-interactive shrink-0 items-center">
                                <ProductIcon className="size-4 text-content-weaker group-hover:text-content" />
                            </span>
                            <span className="flex min-w-0 flex-col">
                                <Typography
                                    variant="caption"
                                    className="truncate text-brand group-hover:underline"
                                >
                                    {product.id}
                                </Typography>
                                <Typography
                                    variant="body"
                                    className="line-clamp-2 text-content-mid group-hover:text-content group-hover:underline"
                                >
                                    {product.name}{' '}
                                    <ArrowUpRightIcon className="inline-block size-3.5 align-text-bottom text-content-weaker group-hover:text-content" />
                                </Typography>
                            </span>
                        </PolymorphicButtonBase>
                        <IconButton
                            label={t('add_product', { ns: 'catalog' })}
                            onClick={() => {
                                onSelect(product);
                            }}
                        >
                            <PlusIcon />
                        </IconButton>
                    </div>
                ))}
            </div>
            {visible.length > shown && (
                <div className="flex justify-center py-tight">
                    <Button
                        onClick={() => {
                            setShown(shown + rowsStep);
                        }}
                    >
                        {t('show_more')}
                    </Button>
                </div>
            )}
        </div>
    );
}

/** Name widths per placeholder row, so the rows don't read as one repeated block. */
const loadingNameWidths = ['w-3/5', 'w-4/5', 'w-1/2'];

// Mirrors a product row (icon, id line, name, add button), as the insight dialog's article list does
function SuggestionRowsLoading(): ReactElement {
    return (
        <div className="flex flex-col divide-y divide-line-weak">
            {loadingNameWidths.map((nameWidth) => (
                <div key={nameWidth} className="flex items-center gap-cozy py-cozy">
                    <Skeleton className="size-4 shrink-0 rounded-default bg-content-weakest/50" />
                    <div className="flex min-w-0 grow flex-col gap-0.5">
                        <div className="flex h-4 items-center">
                            <Skeleton className="h-2.5 w-16 bg-content-weakest/50" />
                        </div>
                        <div className="flex h-5 items-center">
                            <Skeleton className={nameWidth} />
                        </div>
                    </div>
                    <Skeleton className="size-interactive shrink-0 rounded-default" />
                </div>
            ))}
        </div>
    );
}
